/**
 * Signal events panel on the PoR dashboard.
 *
 * Same gate split as ConditionBand (build contract §2.7 — one canonical
 * module gate, no parallel truth sources):
 *   signal_response ON  — the unified Signal Framework v2 log (SignalLog),
 *     reading/writing `signal_events`. The legacy A/B/C/D entry path is
 *     hidden so the dashboard and the Synopsis can never disagree.
 *   signal_response OFF — the legacy panel below: open `por_signal_events`
 *     classified under A/B/C/D with rationale, entered manually (Phase 02).
 */

import { useState } from 'react';
import { Plus, Radio, Check } from 'lucide-react';
import {
  POR_CONDITIONS,
  POR_CONDITION_ORDER,
  type AssembledPor,
  type PorCondition,
} from '../../types/plan-of-record';
import { createSignalEvent, resolveSignalEvent } from '../../lib/planOfRecordService';
import { useModuleGateForWorkspace } from '../../lib/moduleGate';
import { SignalLog } from '../signals/SignalLog';
import { GhostButton, PorCard, PorCardHeader, PorEmptyState, PorModal, PrimaryButton } from './porUi';
import { PorNarrativeMarkdown } from './PorNarrativeMarkdown';

export function SignalEventsPanel({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  // Gate on the RECORD's workspace, not the session's (demo mode / active
  // tenant can differ from the workspace this PoR belongs to).
  const signalV2 = useModuleGateForWorkspace(assembled.record.workspace_id, 'signal_response');
  if (signalV2) {
    return (
      <PorCard>
        <SignalLog
          workspaceId={assembled.record.workspace_id}
          porId={assembled.id}
          canAct
        />
      </PorCard>
    );
  }
  return <LegacySignalEventsPanel assembled={assembled} refresh={refresh} />;
}

function LegacySignalEventsPanel({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const open = assembled.open_signal_events;

  return (
    <PorCard>
      <PorCardHeader
        title="Active Signal Events"
        count={open.length}
        action={
          <GhostButton onClick={() => setAdding(true)}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </span>
          </GhostButton>
        }
      />

      {open.length === 0 ? (
        <PorEmptyState
          icon={<Radio className="h-6 w-6" />}
          title="No open signal events"
          prompt="When something changes — inside or outside the org — record it here and classify it A/B/C/D. This is the instrument feed."
          action={<GhostButton onClick={() => setAdding(true)}>Record a signal event</GhostButton>}
        />
      ) : (
        <div className="divide-y divide-[var(--rule)]">
          {open.map((ev) => {
            const meta = POR_CONDITIONS[ev.condition];
            return (
              <div key={ev.id} className="py-3 flex items-start gap-3 group">
                <span
                  className={`font-mono text-xs font-bold rounded px-1.5 py-0.5 mt-0.5 ${
                    meta.isFailureMode
                      ? 'bg-luminous-coral-100 text-luminous-coral-900'
                      : 'bg-primary-50 text-primary-700'
                  }`}
                >
                  {ev.condition}
                </span>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <PorNarrativeMarkdown source={ev.description} />
                  <div className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] pt-2 border-t border-[var(--rule)] dark:border-[var(--rule)] mt-2">
                    <span className="font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1">
                      Why condition {ev.condition}
                    </span>
                    <PorNarrativeMarkdown source={ev.rationale} />
                  </div>
                  {ev.action_required && (
                    <div className="text-xs text-electric-mustard-900 dark:text-electric-mustard-100 pt-2 border-t border-electric-mustard-500/25 mt-2">
                      <span className="font-semibold block mb-1">Action required</span>
                      <PorNarrativeMarkdown source={ev.action_required} />
                    </div>
                  )}
                </div>
                <button
                  onClick={async () => {
                    await resolveSignalEvent(ev.id, true);
                    await refresh();
                  }}
                  className="text-[var(--text-ter)] hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                  title="Resolve event"
                >
                  <Check className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <SignalEventModal
          porId={assembled.id}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      )}
    </PorCard>
  );
}

function SignalEventModal({
  porId,
  onClose,
  onDone,
}: {
  porId: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [condition, setCondition] = useState<PorCondition>('A');
  const [description, setDescription] = useState('');
  const [rationale, setRationale] = useState('');
  const [actionRequired, setActionRequired] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = description.trim().length > 0 && rationale.trim().length > 0;

  const handleSave = async () => {
    if (!valid) {
      setError('Description and rationale are both required.');
      return;
    }
    setBusy(true);
    setError(null);
    const { error: err } = await createSignalEvent(porId, {
      condition,
      description: description.trim(),
      rationale: rationale.trim(),
      action_required: actionRequired.trim() || null,
    });
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title="Record a signal event"
      subtitle="Every priority shift is one of four conditions. Which is this?"
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Add event'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={label}>Condition</label>
          <div className="grid grid-cols-4 gap-2">
            {POR_CONDITION_ORDER.map((code) => {
              const meta = POR_CONDITIONS[code];
              return (
                <button
                  key={code}
                  onClick={() => setCondition(code)}
                  title={meta.name}
                  className={` border px-2 py-1.5 font-mono text-sm font-bold transition-colors ${
                    condition === code
                      ? meta.isFailureMode
                        ? 'border-luminous-coral-300 bg-luminous-coral-50 text-luminous-coral-900'
                        : 'border-primary-300 bg-primary-50 text-primary-700'
                      : 'border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--surface)]'
                  }`}
                >
                  {code}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-1.5">{POR_CONDITIONS[condition].definition}</p>
        </div>
        <div>
          <label className={label}>What changed / what is being observed</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
            autoFocus
          />
        </div>
        <div>
          <label className={label}>Why this classification</label>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
          />
        </div>
        <div>
          <label className={label}>Action required (optional)</label>
          <input value={actionRequired} onChange={(e) => setActionRequired(e.target.value)} className={field} />
        </div>
        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
